/** Period and comparison state for the Visitors Desk, kept in the URL.
 *  Days are Eastern calendar days, like the API. A period ends on the last
 *  complete day (yesterday), except `today`, which is the live read. */
import { todayEastern, type DateRange } from './visitorsRange'

export type DeskPeriodKind = '7d' | '28d' | '90d' | 'mtd' | '12m' | 'custom' | 'today'
export type DeskCompare = 'both' | 'previous' | 'last_year'
export type DeskTab = 'today' | 'sources' | 'search' | 'flows' | 'articles' | 'issues' | 'audience' | 'trends' | 'revenue'

export type DeskGroup = 'all' | 'nonmember' | 'lapsed' | 'member'
export interface DeskQuery { period: DeskPeriodKind; from?: string; to?: string; compare: DeskCompare; group?: DeskGroup }

export const DESK_GROUPS: { key: DeskGroup; label: string }[] = [
  { key: 'all', label: 'Everyone' },
  { key: 'nonmember', label: 'Non-members' },
  { key: 'lapsed', label: 'Lapsed' },
  { key: 'member', label: 'Members' },
]

/** Tabs that can read today's sessions live. */
export const LIVE_TABS: readonly string[] = ['sources', 'flows', 'articles']

export const PERIOD_OPTIONS: { value: DeskPeriodKind; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '28d', label: 'Last 28 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'mtd', label: 'Month to date' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'custom', label: 'Custom' },
  { value: 'today', label: 'Today (live)' },
]

/** The period choices for one tab: "Today (live)" only where the tab has a live read. */
export function periodOptions(tab: string): { value: DeskPeriodKind; label: string }[] {
  return PERIOD_OPTIONS.filter((o) => o.value !== 'today' || LIVE_TABS.includes(tab))
}

export const COMPARE_OPTIONS: { value: DeskCompare; label: string }[] = [
  { value: 'both', label: 'Previous period + last year' },
  { value: 'previous', label: 'Previous period only' },
  { value: 'last_year', label: 'Last year only' },
]

const ISO = /^\d{4}-\d{2}-\d{2}$/
const KINDS: readonly string[] = PERIOD_OPTIONS.map((o) => o.value)
const COMPARES: readonly string[] = COMPARE_OPTIONS.map((o) => o.value)
const GROUPS: readonly string[] = DESK_GROUPS.map((g) => g.key)
const DEFAULT: DeskQuery = { period: '28d', compare: 'both' }

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

export function deskQueryFromSearch(sp: URLSearchParams, tab: string): DeskQuery {
  const rawCompare = sp.get('compare') ?? ''
  const compare = (COMPARES.includes(rawCompare) ? rawCompare : 'both') as DeskCompare
  const rawGroup = sp.get('g') ?? ''
  const group = (GROUPS.includes(rawGroup) ? rawGroup : 'all') as DeskGroup
  const p = sp.get('period') ?? ''
  if (!KINDS.includes(p)) return { ...DEFAULT, compare, group }
  if (p === 'today') return LIVE_TABS.includes(tab) ? { period: 'today', compare, group } : { ...DEFAULT, compare, group }
  if (p === 'custom') {
    const from = sp.get('from') ?? '', to = sp.get('to') ?? ''
    if (ISO.test(from) && ISO.test(to) && from <= to) return { period: 'custom', from, to, compare, group }
    return { ...DEFAULT, compare, group }
  }
  return { period: p as DeskPeriodKind, compare, group }
}

/** URL params for a query. The defaults (28d, both) are left out so links stay short. */
export function deskQueryToSearch(q: DeskQuery): Record<string, string> {
  const out: Record<string, string> = {}
  if (q.period !== '28d') out.period = q.period
  if (q.period === 'custom' && q.from && q.to) { out.from = q.from; out.to = q.to }
  if (q.compare !== 'both') out.compare = q.compare
  if (q.group && q.group !== 'all') out.g = q.group
  return out
}

/** The API query string for one title (or `all`). */
export function deskQuery(pub: string, q: DeskQuery): string {
  const parts = [`publication=${encodeURIComponent(pub)}`, `period=${q.period}`, `compare=${q.compare}`]
  if (q.group && q.group !== 'all') parts.push(`group=${q.group}`)
  if (q.period === 'custom' && q.from && q.to) parts.push(`from=${q.from}`, `to=${q.to}`)
  return parts.join('&')
}

/** Old Visitors URLs (`range=…`) as desk params. Other keys (filters) are kept. */
export function legacyToDesk(sp: URLSearchParams, now: Date = new Date()): URLSearchParams {
  const out = new URLSearchParams()
  const range = sp.get('range')
  if (range === '7d' || range === '90d' || range === '12m' || range === 'mtd') out.set('period', range)
  else if (range === '30d') out.set('period', '28d')
  else if (range === 'today' || range === 'yesterday') {
    const today = todayEastern(now)
    const day = range === 'today' ? today : addDays(today, -1)
    out.set('period', 'custom'); out.set('from', day); out.set('to', day)
  } else if (range === 'custom') {
    const from = sp.get('from') ?? '', to = sp.get('to') ?? ''
    if (ISO.test(from) && ISO.test(to) && from <= to) { out.set('period', 'custom'); out.set('from', from); out.set('to', to) }
  }
  for (const [k, v] of sp) if (k !== 'range' && k !== 'from' && k !== 'to') out.append(k, v)
  return out
}

const FIXED_DAYS: Partial<Record<DeskPeriodKind, number>> = { '7d': 7, '28d': 28, '90d': 90, '12m': 365 }

/** The desk period as concrete dates, as the API resolves it when the data is
 *  up to date: ending yesterday; month to date from the 1st (on the 1st, the
 *  whole previous month); today is that one day. */
export function deskDates(q: DeskQuery, today: string): { from: string; to: string } {
  if (q.period === 'today') return { from: today, to: today }
  if (q.period === 'custom' && q.from && q.to) return { from: q.from, to: q.to }
  const end = addDays(today, -1)
  if (q.period === 'mtd') return { from: `${end.slice(0, 8)}01`, to: end }
  return { from: addDays(end, 1 - (FIXED_DAYS[q.period] ?? 28)), to: end }
}

/** The desk period in the old `{from, to, preset}` shape, for the Revenue body. */
export function revenueRange(q: DeskQuery, today: string): DateRange {
  return { ...deskDates(q, today), preset: 'custom' }
}
